import { useEffect, useRef } from 'react';

const solutions = [
  {
    icon: 'ri-filter-3-line',
    title: 'Funnel Yapısı',
    description: 'Ziyaretçiyi adım adım harekete geçirir. Visitor → Interest → Action akışı otomatik çalışır.',
    color: 'text-gold',
    bg: 'bg-gold/10',
  },
  {
    icon: 'ri-whatsapp-line',
    title: 'Anlık İletişim',
    description: 'WhatsApp, form ve hızlı arama butonları ile potansiyel müşteri anında size ulaşır.',
    color: 'text-green-400',
    bg: 'bg-green-400/10',
  },
  {
    icon: 'ri-megaphone-line',
    title: 'Kampanya Sayfaları',
    description: 'Her reklam kampanyası için özel tasarlanmış landing page\'ler ile dönüşümü maksimize eder.',
    color: 'text-neon',
    bg: 'bg-neon/10',
  },
  {
    icon: 'ri-cursor-line',
    title: 'Dönüşüm Odaklı Akış',
    description: 'Her element, ziyaretçiyi bir sonraki adıma yönlendirecek şekilde stratejik olarak konumlandırılır.',
    color: 'text-purple-400',
    bg: 'bg-purple-400/10',
  },
];

export default function SolutionSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.sol-item').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateX(0)';
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="solution"
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        background: 'radial-gradient(ellipse at 0% 50%, rgba(240,180,41,0.05) 0%, transparent 60%), linear-gradient(180deg, #0a0a0f 0%, #0d0d18 100%)',
      }}
    >
      <div className="absolute top-0 left-0 right-0 section-divider" />

      <div className="max-w-7xl mx-auto px-4 md:px-8" ref={ref}>
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">

          {/* Left */}
          <div className="flex-1 max-w-xl">
            <div className="inline-flex items-center gap-2 bg-green-900/20 border border-green-500/20 px-4 py-2 rounded-full mb-6">
              <i className="ri-shield-check-line text-green-400 text-sm" />
              <span className="text-green-400 text-sm font-semibold">Çözüm</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 tracking-tight leading-tight">
              Web Sitesi Değil,{' '}
              <br />
              <span className="text-gold-gradient">Dijital Satış Makinesi</span>
            </h2>

            {/* Sub-statement */}
            <div className="flex flex-col gap-1 mb-6 pl-1 border-l-2 border-gold/30">
              <p className="text-gray-200 text-base font-semibold leading-snug">
                Her ziyaretçi bir aksiyona zorlanır.
              </p>
              <p className="text-gray-400 text-sm leading-snug">
                Sistem, kullanıcıyı yönlendirir ve dönüşüm üretir.
              </p>
            </div>

            <p className="text-gray-400 text-lg mb-8 leading-relaxed">
              Sadece görünmek için değil, müşteri kazanmak için kuruyoruz. Her eleman, her buton, her sayfa tek bir amaca hizmet eder:{' '}
              <strong className="text-gold font-extrabold">size müşteri getirmek.</strong>
            </p>

            <div className="space-y-4">
              {solutions.map((item) => (
                <div
                  key={item.title}
                  className="sol-item flex items-start gap-4 glass rounded-xl p-4 border border-white/5 card-hover"
                  style={{ opacity: 0, transform: 'translateX(-20px)', transition: 'all 0.5s ease' }}
                >
                  <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${item.bg} flex-shrink-0`}>
                    <i className={`${item.icon} text-xl ${item.color}`} />
                  </div>
                  <div>
                    <h3 className="font-bold text-white mb-1">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Visual flow */}
          <div className="flex-1 max-w-sm w-full">
            <div className="glass-gold rounded-3xl p-8 relative overflow-hidden">
              <div className="absolute inset-0 opacity-5" style={{ background: 'radial-gradient(circle at 50% 0%, #f0b429, transparent)' }} />
              <h3 className="text-center text-gold font-bold text-sm mb-8 uppercase tracking-widest">Dönüşüm Akışı</h3>
              <div className="space-y-0">
                {[
                  { step: '01', icon: 'ri-advertisement-line', label: 'Reklam Görülür', sub: 'Google / Meta reklamı', color: 'border-gold/40 bg-gold/10 text-gold' },
                  { step: '02', icon: 'ri-eye-line', label: 'Sayfa Ziyaret', sub: 'Funnel landing page', color: 'border-neon/40 bg-neon/10 text-neon' },
                  { step: '03', icon: 'ri-hand-heart-line', label: 'Aksiyon Alınır', sub: 'WhatsApp / Form / Arama', color: 'border-green-400/40 bg-green-400/10 text-green-400' },
                  { step: '04', icon: 'ri-user-star-line', label: 'Müşteri Olur', sub: 'Dönüşüm tamamlandı', color: 'border-yellow-400/40 bg-yellow-400/10 text-yellow-400' },
                ].map((item, idx) => (
                  <div key={item.step} className="relative">
                    <div className={`glass border rounded-2xl p-4 flex items-center gap-4 ${item.color}`}>
                      <span className="text-xs font-black opacity-50">{item.step}</span>
                      <div className={`w-10 h-10 flex items-center justify-center rounded-xl border ${item.color}`}>
                        <i className={`${item.icon} text-lg`} />
                      </div>
                      <div>
                        <div className="font-bold text-white text-sm">{item.label}</div>
                        <div className="text-gray-500 text-xs">{item.sub}</div>
                      </div>
                    </div>
                    {idx < 3 && (
                      <div className="flex justify-center my-1">
                        <div className="flex flex-col items-center gap-0.5">
                          <div className="w-px h-3 bg-gold/30" />
                          <i className="ri-arrow-down-s-line text-gold/40 text-xs" />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
